import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { useAppState } from "Context/StateContext";
import { Agent } from "Models/agent";
import { useTranslation } from "react-i18next";
import AgentService from "Services/agent.service";
import CompanyService from "Services/company.service";
import TimezoneService from "Services/timezone.service";
import UserService from "Services/user.service";
import { Status } from "Util/enums";
import { AgentKeys } from "Util/queries";

export const useAgentCreateAndEditActions = () => {
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const getTimezoneList = () => useQuery(["timezones.list"], () => TimezoneService.getAll().then(({ data }) => data), {
    initialData() {
      return [];
    },
  });

  const getConsultantList = () => useQuery(
    ["consultant.list"],
    () =>
      UserService.getAll({
        include: JSON.stringify([
          {
            association: "usersCompanies",
            required: true,
            where: { status: Status.ACTIVE, companyId: currentUser?.companyId },
          },
        ]),
      }).then(({ data }) => data),
    {
      enabled: !!currentUser?.companyId,
      initialData() {
        return [];
      },
    }
  );


  const useInviteAgent = (params: { onSuccess?: () => void } = {}) => {
    const { onSuccess } = params;
    return useMutation({
      mutationKey: AgentKeys.invite(),
      mutationFn: ({ agent }: { agent: Agent }) => {
        const createdCompany = agent.isShipeeziNetwork === false;
        return AgentService.inviteToShipeezi({ agentId: agent.id, createdCompany });
      },
      onSuccess(data, variables, context) {
        NotificationToast.success(translate("inviteSentSuccessfully"));
        onSuccess?.();
      },
    });
  };

  const useCheckToken = (params: {
    onSuccess?: (response: AxiosResponse<{
      exists: boolean;
      isActive: boolean;
    }, any>) => void
    onError?: () => void
  } = {}) => {
    const { onSuccess, onError } = params;
    return useMutation({
      mutationKey: AgentKeys.checkToken(),
      mutationFn: ({ token }: { token: string }) => CompanyService.checkCompanyToken({ token }),
      onSuccess(response, variables, context) {
        onSuccess?.(response);
      },
      onError(error, variables, context) {
        onError?.();
      },
    });
  };

  const useRevokeConnection = (params: {
    onSuccess?: () => void
  } = {}) => {
    const { onSuccess } = params;
    return useMutation({
      mutationKey: AgentKeys.revoke(),
      mutationFn: ({ agentId, companyId }: { agentId: number, companyId: number }) => AgentService.revoke({ agentId, companyId }),
      onSuccess(response, variables, context) {
        onSuccess?.();
      },
    });
  };

  return {
    getTimezoneList,
    getConsultantList,
    useInviteAgent,
    useCheckToken,
    useRevokeConnection,
  };
}
